/* ==========================================================================
 * Personalize badge + collapsible engraving box  —  MOBILE ONLY
 * --------------------------------------------------------------------------
 * TOZ24 / Cloudlift "Live Product Options" integration.
 *
 * Context (verified): the engraving field (textarea[name="properties[Text]"])
 * and the product gallery are injected AT RUNTIME by the Cloudlift app.
 *   - Cloudlift replaces the theme .product-gallery-container with its own
 *     .cl-po--gallery-wrapper inside the stable .product-gallery-sticky.
 *   - The engraving field lives inside the server-rendered app-block
 *     [id*="live_product_options_app_block"] in the .product-information
 *     column, OUTSIDE the <form>; it is cart-bound only via its
 *     form="product-form-<section.id>__main-product" attribute. product.js
 *     snapshots the cart with new FormData(form-by-id), so MOVING the mount
 *     anywhere is cart-safe as long as name=/form= are untouched and it is
 *     never cloned/duplicated.
 *
 * Strategy (only when matchMedia('(max-width: 767px)')):
 *   1. Wait (MutationObserver) for the Cloudlift gallery + engraving mount.
 *   2. Inject an overlay layer over the first product image, into the stable
 *      .product-grid-container (NOT the sticky, whose children Cloudlift owns).
 *   3. MOVE the whole app-block mount (not clone) into a box in the layer.
 *   4. Tap badge -> expand (slide up at image bottom), hide badge.
 *      Tap outside / Esc -> collapse; badge returns; the field stays mounted.
 *   5. Re-assert idempotently on Cloudlift re-renders; on desktop restore the
 *      mount to its original location; tear down on Shopify section re-render.
 * ========================================================================== */
(function () {
  'use strict';

  var BREAKPOINT = '(max-width: 767px)'; // keep in sync with personalize-badge.css
  var OBS_OPTS = { childList: true, subtree: true };

  var SEL = {
    sticky: '.product-gallery-sticky',
    galleryMain: '.cl-po--gallery-main',
    galleryWrap: '.cl-po--gallery-wrapper',
    mount: '[id*="live_product_options_app_block"]', // Cloudlift app-block (holds the field)
    dlo: '[data-live-options]',
    textarea: 'textarea.cl-po--input, textarea[name="properties[Text]"]'
  };

  // Magic-wand icon — copied 1:1 from the reference CTA (gradient id namespaced).
  var WAND_SVG = '<svg width="18" height="17" fill="none" xmlns="http://www.w3.org/2000/svg" role="img" aria-hidden="true" viewBox="0 0 18 17"><defs><linearGradient id="toz_pers_grad" x1="0" y1="8.5" x2="18" y2="8.5" gradientUnits="userSpaceOnUse"><stop stop-color="#682F87"></stop><stop offset="0.495" stop-color="#3B53EB"></stop><stop offset="1" stop-color="#DD2525"></stop></linearGradient></defs><path d="M7.75 0.65625C7.78125 0.5625 7.875 0.5 8 0.5C8.09375 0.5 8.1875 0.5625 8.21875 0.65625L8.65625 1.84375L9.84375 2.28125C9.9375 2.3125 10 2.40625 10 2.5C10 2.625 9.9375 2.71875 9.84375 2.75L8.65625 3.1875L8.21875 4.34375C8.1875 4.4375 8.09375 4.5 8 4.5C7.875 4.5 7.78125 4.4375 7.75 4.34375L7.3125 3.1875L6.125 2.75C6.03125 2.71875 6 2.625 6 2.5C6 2.40625 6.03125 2.3125 6.125 2.28125L7.3125 1.84375L7.75 0.65625ZM15.4688 0.96875L16.5312 2.03125C17.125 2.625 17.125 3.5625 16.5312 4.15625L4.625 16.0625C4.03125 16.6562 3.09375 16.6562 2.5 16.0625L1.4375 15C0.84375 14.4062 0.84375 13.4688 1.4375 12.875L13.3438 0.96875C13.9375 0.375 14.875 0.375 15.4688 0.96875ZM14.4062 2L10.9688 5.4375L12.0625 6.53125L15.4688 3.09375L14.4062 2ZM3.5625 15L11 7.59375L9.90625 6.5L2.5 13.9375L3.5625 15ZM0.21875 4.1875L2 3.5L2.65625 1.75C2.6875 1.59375 2.84375 1.5 3 1.5C3.125 1.5 3.28125 1.59375 3.3125 1.75L4 3.5L5.75 4.1875C5.90625 4.21875 6 4.375 6 4.5C6 4.65625 5.90625 4.8125 5.75 4.84375L4 5.5L3.3125 7.28125C3.28125 7.40625 3.125 7.5 3 7.5C2.84375 7.5 2.6875 7.40625 2.65625 7.28125L2 5.5L0.21875 4.84375C0.0625 4.8125 0 4.65625 0 4.5C0 4.375 0.0625 4.21875 0.21875 4.1875ZM11.2188 12.1875L13 11.5L13.6562 9.75C13.6875 9.59375 13.8438 9.5 14 9.5C14.125 9.5 14.2812 9.59375 14.3125 9.75L15 11.5L16.75 12.1875C16.9062 12.2188 17 12.375 17 12.5C17 12.6562 16.9062 12.8125 16.75 12.8438L15 13.5L14.3125 15.2812C14.2812 15.4062 14.125 15.5 14 15.5C13.8438 15.5 13.6875 15.4062 13.6562 15.2812L13 13.5L11.2188 12.8438C11.0625 12.8125 11 12.6562 11 12.5C11 12.375 11.0625 12.2188 11.2188 12.1875Z" fill="url(#toz_pers_grad)"></path></svg>';

  // Price shown inside the badge: regular engraving price struck through, then free.
  var PRICE_COMPARE = '5,99\u00a0\u20ac'; // non-breaking space + euro sign
  var PRICE_NOW = '0\u00a0\u20ac';

  // One-time reveal sequence, keep in sync with personalize-badge.css:
  //   window load -> REVEAL_DELAY -> pop-in (POP_MS) -> INTRO_DELAY -> intro (INTRO_MS)
  // Delays only count while the tab is visible.
  var REVEAL_DELAY = 800;       // ms after load + field ready before the badge appears
  var LOAD_FALLBACK_MS = 3000;  // reveal anyway if 'load' hasn't fired by then (slow 3rd parties)
  var POP_MS = 400;             // pop-in on first reveal (.is-pop)
  var INTRO_DELAY = 400;        // ms after the pop-in ends before the ring + text sweep
  var INTRO_MS = 2200;          // ring duration (fallback cleanup if animationend never fires)

  var mq = window.matchMedia(BREAKPOINT);
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var uid = 0;

  function init(marker) {
    if (marker.dataset.pzInit) return; // idempotent: never bind a marker twice
    marker.dataset.pzInit = '1';

    var grid = marker.closest('.product-grid-container');
    if (!grid) return;
    var sectionId = marker.getAttribute('data-personalize-section') || ('pz' + (++uid));

    var state = {
      placeholder: null, // marks the mount's original location, for desktop restore
      layer: null, badge: null, box: null, head: null,
      timer: 0, resizeTimer: 0, listening: false,
      kbd: false, raf: 0,
      mo: null, ro: null, observedSticky: null,
      loaded: false, loadTimer: 0, fieldReady: false, visListening: false,
      revealed: false, revealTimer: 0,
      popDone: false, popTimer: 0, popBadge: null,
      introDone: false, introTimer: 0, introEndTimer: 0, introBadge: null
    };

    function getSticky() { return grid.querySelector(SEL.sticky); }
    function valueLen(m) { var t = m && m.querySelector(SEL.textarea); return (t && t.value) ? t.value.length : 0; }

    // Resolve the Cloudlift mount WITHIN this section's grid (not document-global),
    // so two product sections on one page can never grab each other's field.
    function findMount() {
      var all = grid.querySelectorAll(SEL.mount);
      for (var i = 0; i < all.length; i++) { if (all[i].querySelector(SEL.textarea)) return all[i]; }
      if (all.length) return all[0];
      var dlo = grid.querySelector(SEL.dlo);
      return dlo ? (dlo.closest(SEL.mount) || dlo) : null;
    }

    function buildLayer() {
      var boxId = 'personalize-box-' + sectionId;
      var layer = document.createElement('div');
      layer.className = 'personalize-layer is-collapsed';

      var badge = document.createElement('button');
      badge.type = 'button';
      badge.className = 'personalize-badge';
      badge.setAttribute('aria-expanded', 'false');
      badge.setAttribute('aria-controls', boxId);
      badge.setAttribute('aria-label', 'Personalisieren');
      badge.innerHTML = '<span class="personalize-badge__inner">' + WAND_SVG +
        '<span class="personalize-badge__text">Personalisieren</span></span>';

      var box = document.createElement('div');
      box.className = 'personalize-box';
      box.id = boxId;
      box.setAttribute('role', 'group');
      box.setAttribute('aria-label', 'Personalisierung');

      // Slim price header above the engraving field (first child of the box, never
      // inside the Cloudlift mount). Purely visual: the badge aria-label already
      // announces the price. Own gradient id so it never depends on the badge SVG.
      var head = document.createElement('div');
      head.className = 'personalize-box__head';
      head.setAttribute('aria-hidden', 'true');
      head.innerHTML = '<span class="personalize-box__title">' +
        WAND_SVG.replace(/toz_pers_grad/g, 'toz_pers_grad_head') +
        '<span class="personalize-box__label">Personalisierung</span></span>' +
        '<span class="personalize-box__price"><s class="personalize-box__compare">' + PRICE_COMPARE +
        '</s><span class="personalize-box__free">' + PRICE_NOW + '</span></span>';
      box.appendChild(head);

      layer.appendChild(badge);
      layer.appendChild(box);
      badge.addEventListener('click', function (e) { e.preventDefault(); expand(); });

      return { layer: layer, badge: badge, box: box, head: head };
    }

    // The layer lives in .product-grid-container (the stable theme wrapper), NOT
    // in .product-gallery-sticky whose children Cloudlift owns/replaces.
    function ensureLayer() {
      if (!getSticky()) return false; // wait until the gallery column has mounted
      if (state.layer && grid.contains(state.layer)) return true;
      var parts = buildLayer();
      state.layer = parts.layer;
      state.badge = parts.badge;
      state.box = parts.box;
      state.head = parts.head;
      if (!state.revealed) setBadgeInert(true); // invisible until the reveal sequence
      grid.appendChild(state.layer);
      return true;
    }

    // Size/position the layer to exactly overlap the first gallery image
    // (measured relative to the grid, which is the layer's offset parent).
    function positionLayer() {
      if (!state.layer) return;
      var sticky = getSticky();
      if (!sticky) return;
      var img = sticky.querySelector(SEL.galleryMain) || sticky.querySelector(SEL.galleryWrap);
      var ref = grid.getBoundingClientRect();
      var box = (img && img.getBoundingClientRect().height > 0) ? img : sticky;
      var r = box.getBoundingClientRect();
      var st = state.layer.style;
      st.top = (r.top - ref.top) + 'px';
      st.left = (r.left - ref.left) + 'px';
      st.width = r.width + 'px';
      st.height = (box === sticky ? r.width : r.height) + 'px';
    }

    // Re-measure when the gallery image loads/decodes or swaps (height change
    // without a childList mutation, so the MutationObserver would not catch it).
    function observeSticky() {
      if (!state.ro) return;
      var sticky = getSticky();
      if (sticky && state.observedSticky !== sticky) {
        state.ro.disconnect();
        state.ro.observe(sticky);
        state.observedSticky = sticky;
      }
    }

    function rememberOrigin(mount) {
      if (state.placeholder || !mount || !mount.parentNode) return;
      state.placeholder = document.createComment('personalize-mount-anchor');
      mount.parentNode.insertBefore(state.placeholder, mount);
    }

    function moveMountIn() {
      var mount = findMount();
      if (!mount || !state.box) return false;
      if (!state.box.contains(mount)) {
        rememberOrigin(mount);
        state.box.appendChild(mount);
      }
      // Guarantee EXACTLY ONE mount in the box. If Cloudlift re-renders and injects
      // a fresh app-block (which findMount may pick up), keep the single survivor
      // with the longest engraving value so the user's input is never lost AND
      // properties[Text] is never serialised twice into /cart/add.
      var ms = state.box.querySelectorAll(SEL.mount);
      if (ms.length > 1) {
        var keep = mount, keepLen = valueLen(mount), i;
        for (i = 0; i < ms.length; i++) { var l = valueLen(ms[i]); if (l > keepLen) { keep = ms[i]; keepLen = l; } }
        for (i = 0; i < ms.length; i++) { if (ms[i] !== keep) ms[i].remove(); }
      }
      // Keep the price header first, even if a re-render put content before it.
      if (state.head && state.box.firstElementChild !== state.head) {
        state.box.insertBefore(state.head, state.box.firstChild);
      }
      return true;
    }

    function restoreMount() {
      collapse();
      if (!state.box) return;
      var mount = state.box.querySelector(SEL.mount);
      if (!mount) return;
      if (state.placeholder && state.placeholder.parentNode) {
        state.placeholder.parentNode.insertBefore(mount, state.placeholder);
      } else {
        // Original spot was re-rendered away — re-home into the info column rather
        // than strand the field inside the (display:none on desktop) overlay layer.
        var dest = grid.querySelector('.product-information--inner') ||
                   grid.querySelector('.product-information') ||
                   document.querySelector('.product-information--inner');
        if (dest) dest.appendChild(mount);
      }
    }

    function setBadgeInert(inert) {
      if (!state.badge) return;
      if (!state.revealed) inert = true; // never focusable before the badge is revealed
      if (inert) { state.badge.setAttribute('tabindex', '-1'); state.badge.setAttribute('aria-hidden', 'true'); }
      else { state.badge.removeAttribute('tabindex'); state.badge.removeAttribute('aria-hidden'); }
    }

    // ----- Mobile keyboard tracking -------------------------------------------
    // Pin the box to the bottom of the VISUAL viewport (just above the on-screen
    // keyboard) using the established jitter-free pattern: position:fixed + a GPU
    // transform. iOS Safari has no `interactive-widget` support (can't let the
    // keyboard resize the layout) and scrolling the page fights iOS (creep + wrong
    // landing), so we anchor with a compositor transform instead.
    //   - translate3d(0, vvBottom, 0) moves the (fixed, top:0) box down to the
    //     visual-viewport bottom; translateY(calc(-100% - gap)) lifts it by its own
    //     height + gap so its bottom sits `gap` above the keyboard. Using -100%
    //     (not a JS-measured height) keeps it anchored as the field grows.
    //   - Updated on BOTH visualViewport resize (keyboard open/close, height) and
    //     scroll (offsetTop changes), coalesced into one rAF so it rides the
    //     keyboard animation smoothly without layout thrash.
    function positionBox() {
      state.raf = 0;
      var vv = window.visualViewport;
      if (!vv || !state.kbd || !state.box) return;
      var y = vv.offsetTop + vv.height; // visual-viewport bottom in layout coords
      state.box.style.transform = 'translate3d(0,' + y + 'px,0) translateY(calc(-100% - 8px))';
    }
    function scheduleBox() {
      if (state.raf || !state.kbd) return;
      state.raf = requestAnimationFrame(positionBox);
    }
    function startKeyboardTracking() {
      var vv = window.visualViewport;
      if (!vv || state.kbd) return; // no VisualViewport -> CSS keeps the image-bottom box
      state.kbd = true;
      state.layer.classList.add('is-kbd');
      positionBox();                          // initial anchor (pre-keyboard; resize corrects it)
      vv.addEventListener('resize', scheduleBox);
      vv.addEventListener('scroll', scheduleBox);
    }
    function stopKeyboardTracking() {
      if (!state.kbd) return;
      state.kbd = false;
      var vv = window.visualViewport;
      if (vv) {
        vv.removeEventListener('resize', scheduleBox);
        vv.removeEventListener('scroll', scheduleBox);
      }
      if (state.raf) { cancelAnimationFrame(state.raf); state.raf = 0; }
      if (state.layer) state.layer.classList.remove('is-kbd');
      if (state.box) state.box.style.transform = ''; // back to the CSS image-bottom box
    }

    function expand() {
      if (!state.layer) return;
      state.layer.classList.remove('is-collapsed');
      state.layer.classList.add('is-expanded');
      state.badge.setAttribute('aria-expanded', 'true');
      // Start tracking BEFORE focus so baseH is the true pre-keyboard height.
      if (mq.matches) startKeyboardTracking();
      var ta = state.box.querySelector(SEL.textarea);
      if (ta) {
        // The box is hidden via opacity only (never visibility/display), so the field is
        // ALWAYS focusable; focus it SYNCHRONOUSLY inside the tap so mobile opens the
        // on-screen keyboard. {preventScroll:true} suppresses iOS's native scroll-into-view
        // (which scrolled the page down to the field); the box is anchored above the
        // keyboard by our own fixed+transform, so no native scroll is needed.
        ta.focus({ preventScroll: true });
      }
      setBadgeInert(true); // after focus(): blurring the just-tapped badge must not interfere
      if (!state.listening) {
        document.addEventListener('pointerdown', onOutside, true);
        document.addEventListener('keydown', onKey, true);
        state.listening = true;
      }
    }

    function collapse() {
      if (!state.layer) return;
      stopKeyboardTracking();
      state.layer.classList.add('is-collapsed');
      state.layer.classList.remove('is-expanded');
      if (state.badge) { state.badge.setAttribute('aria-expanded', 'false'); setBadgeInert(false); }
      if (state.listening) {
        document.removeEventListener('pointerdown', onOutside, true);
        document.removeEventListener('keydown', onKey, true);
        state.listening = false;
      }
    }

    function onOutside(e) {
      if (!state.layer) return;
      if (state.box.contains(e.target) || state.badge.contains(e.target)) return;
      var returnFocus = state.box.contains(document.activeElement);
      collapse();
      if (returnFocus && state.badge) state.badge.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape' || e.key === 'Esc') { collapse(); if (state.badge) state.badge.focus(); }
    }

    // ----- One-time reveal sequence ------------------------------------------
    // The badge stays invisible (opacity 0, pointer-events none, inert) until the
    // field is ready AND window 'load' has fired (or LOAD_FALLBACK_MS passed, so it
    // never stays hidden forever), then REVEAL_DELAY later it appears with a pop-in;
    // INTRO_DELAY after the pop ends the intro (ring + text sweep) plays. Delays only
    // count while the tab is visible: a hidden tab cancels the pending timer and
    // the next 'visibilitychange' -> visible re-arms it. Everything runs ONCE per
    // init (revealed / popDone / introDone): collapse/re-show keeps the plain CSS
    // fade and a layer rebuilt by ensureLayer() shows immediately (see reassert).
    function pageHidden() {
      return !!document.visibilityState && document.visibilityState !== 'visible';
    }
    function onVisibility() {
      if (pageHidden()) {
        clearTimeout(state.revealTimer); state.revealTimer = 0;
        clearTimeout(state.introTimer); state.introTimer = 0;
        return;
      }
      maybeReveal();
      maybeIntro();
    }
    function stopVisibility() {
      if (!state.visListening) return;
      document.removeEventListener('visibilitychange', onVisibility);
      state.visListening = false;
    }
    function onPageLoad() {
      window.removeEventListener('load', onPageLoad);
      clearTimeout(state.loadTimer);
      state.loadTimer = 0;
      state.loaded = true;
      maybeReveal();
    }

    function maybeReveal() {
      if (state.revealed || state.revealTimer || !state.fieldReady) return;
      if (!state.loaded) {
        // Safety net: don't let slow third-party scripts/images hold the badge back.
        if (!state.loadTimer) state.loadTimer = setTimeout(onPageLoad, LOAD_FALLBACK_MS);
        return;
      }
      if (pageHidden()) return; // armed by onVisibility once the tab is visible
      state.revealTimer = setTimeout(reveal, REVEAL_DELAY);
    }
    function reveal() {
      state.revealTimer = 0;
      if (state.revealed || !state.layer) return;
      state.revealed = true;
      state.layer.classList.add('is-ready');
      setBadgeInert(state.layer.classList.contains('is-expanded'));
      if (reduceMotion.matches) { // just appear: no pop, no intro
        state.popDone = state.introDone = true;
        stopVisibility();
        return;
      }
      maybePop();
    }

    function maybePop() {
      if (state.popDone || !state.badge) return;
      state.popDone = true;
      var badge = state.badge;
      state.popBadge = badge;
      badge.addEventListener('animationend', onPopEnd);
      badge.classList.add('is-pop');
      state.popTimer = setTimeout(finishPop, POP_MS + 1000); // e.g. cancelled by expand()
    }
    function onPopEnd(e) {
      if (e.animationName === 'pz-pop' && e.target === state.popBadge) finishPop();
    }
    function finishPop() {
      endPop();
      maybeIntro();
    }
    function endPop() {
      clearTimeout(state.popTimer);
      state.popTimer = 0;
      var badge = state.popBadge;
      if (!badge) return;
      state.popBadge = null;
      badge.removeEventListener('animationend', onPopEnd);
      badge.classList.remove('is-pop');
    }

    function maybeIntro() {
      if (state.introDone || state.introTimer) return;
      if (!state.popDone || state.popBadge) return; // only after the pop-in has ended
      if (pageHidden()) return;                     // armed by onVisibility
      state.introTimer = setTimeout(playIntro, INTRO_DELAY);
    }
    function playIntro() {
      state.introTimer = 0;
      if (state.introDone) return;
      state.introDone = true;
      stopVisibility(); // sequence complete
      var badge = state.badge;
      // Already expanded (badge hidden) or switched to desktop: skip, never replay.
      if (!badge || !mq.matches || !state.layer.classList.contains('is-collapsed')) return;
      state.introBadge = badge;
      badge.addEventListener('animationend', onIntroEnd);
      badge.classList.add('is-intro');
      state.introEndTimer = setTimeout(endIntro, INTRO_MS + 1000);
    }
    // End on the ring itself: the shorter text sweep (pz-sweep) also bubbles here.
    function onIntroEnd(e) {
      if (e.animationName === 'pz-intro-spin') endIntro();
    }
    function endIntro() {
      clearTimeout(state.introEndTimer);
      state.introEndTimer = 0;
      var badge = state.introBadge;
      if (!badge) return;
      state.introBadge = null;
      badge.removeEventListener('animationend', onIntroEnd);
      badge.classList.remove('is-intro');
    }

    // Idempotent — safe to call as often as the observer fires.
    function reassert() {
      state.timer = 0;
      if (!mq.matches) return;
      // Pause the observer around our own DOM writes so ensureLayer()/moveMountIn()
      // can't re-trigger reassert (childList mutations inside the observed grid).
      if (state.mo) state.mo.disconnect();
      if (ensureLayer()) {
        moveMountIn();
        observeSticky();
        positionLayer();
        if (state.box.querySelector(SEL.textarea)) {
          state.fieldReady = true;
          // After the one-time reveal a rebuilt layer shows at once (no wait, no pop).
          if (state.revealed) state.layer.classList.add('is-ready');
          else maybeReveal();
        }
      }
      if (state.mo) state.mo.observe(grid, OBS_OPTS);
    }
    // Coalesce bursts of mutations. setTimeout (not requestAnimationFrame) so it
    // still fires when the product page is opened in a background/hidden tab.
    function scheduleReassert() {
      if (state.timer) return;
      state.timer = setTimeout(reassert, 0);
    }

    function onResize() {
      clearTimeout(state.resizeTimer);
      state.resizeTimer = setTimeout(function () {
        if (mq.matches) positionLayer();
        else restoreMount();
      }, 120);
    }
    function handleMedia() {
      if (mq.matches) scheduleReassert();
      else restoreMount(); // back to the info column; CSS keeps the layer hidden
    }

    state.mo = new MutationObserver(scheduleReassert);
    state.mo.observe(grid, OBS_OPTS);
    if (window.ResizeObserver) state.ro = new ResizeObserver(function () { positionLayer(); });
    window.addEventListener('resize', onResize);
    if (mq.addEventListener) mq.addEventListener('change', handleMedia);
    else if (mq.addListener) mq.addListener(handleMedia);
    // Script is deferred, so 'load' may already have fired.
    if (document.readyState === 'complete') state.loaded = true;
    else window.addEventListener('load', onPageLoad);
    document.addEventListener('visibilitychange', onVisibility);
    state.visListening = true;

    // Teardown for Shopify Theme Editor section re-render (shopify:section:unload),
    // so observers/listeners don't leak and accumulate across edits.
    marker.__pzTeardown = function () {
      stopKeyboardTracking();
      clearTimeout(state.timer);
      clearTimeout(state.resizeTimer);
      if (state.mo) state.mo.disconnect();
      if (state.ro) state.ro.disconnect();
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onPageLoad);
      stopVisibility();
      clearTimeout(state.loadTimer);
      clearTimeout(state.revealTimer);
      clearTimeout(state.introTimer);
      state.loadTimer = state.revealTimer = state.introTimer = 0;
      endIntro();
      endPop();
      if (mq.removeEventListener) mq.removeEventListener('change', handleMedia);
      else if (mq.removeListener) mq.removeListener(handleMedia);
      if (state.listening) {
        document.removeEventListener('pointerdown', onOutside, true);
        document.removeEventListener('keydown', onKey, true);
        state.listening = false;
      }
    };

    handleMedia();
    scheduleReassert();
  }

  function boot(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var markers = scope.querySelectorAll('.personalize-badge-mount');
    for (var i = 0; i < markers.length; i++) init(markers[i]);
  }
  function teardown(root) {
    if (!root || !root.querySelectorAll) return;
    var markers = root.querySelectorAll('.personalize-badge-mount');
    for (var i = 0; i < markers.length; i++) { if (markers[i].__pzTeardown) markers[i].__pzTeardown(); }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  // Shopify Theme Editor: re-init / clean up when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) { teardown(e.target); });
})();
